import { injectable, interfaces } from 'inversify';
import { makeObservable, observable } from 'mobx';
import { ACDLError } from '../../__legacy__/models/error.model';
import { IModelImageDto } from '../../__legacy__/modules/annotation/annotations.interface';
import { IImageAnnotationsResponse } from '../../__legacy__/modules/annotation/submodules/segmentations/segmentationContextMenu/segmentationsApi.service';
import { IDatasetDetails, IDatasetPreviewImage } from '../../__legacy__/modules/datasets/datasetsDetails.store';
import { IPredictionImage } from './jobPredictions.model';
import { IJobImagePredictionDto } from './jobs.api.model';

const DEFAULT_VALUES = {
  jobId: null,
  imageId: null,
  predictionDetails: null,
  images: [],
  datasets: new Map(),
  datasetsCache: new Map(),
  imageDetailsCache: new Map(),
  metricsCache: new Map(),
  annotationsCache: new Map(),
  imagesObjectUrl: new Map(),
  threshold: null,
};

export interface IPredictionInspectorStore {
  jobId: string | null;
  imageId: string | null;
  images: IModelImageDto[];
  datasetsCache: Map<string, IDatasetDetails | Promise<IDatasetDetails | ACDLError>>;
  imageDetailsCache: Map<string, IDatasetPreviewImage | Promise<IDatasetPreviewImage | ACDLError>>;
  metricsCache: Map<string, IJobImagePredictionDto>;
  annotationsCache: Map<string, IImageAnnotationsResponse | Promise<IImageAnnotationsResponse | ACDLError>>;
  imagesObjectUrl: Map<string, string>;
  predictionDetails: IPredictionImage | null;
  image: IModelImageDto | null;
  indexOf: number;
  total: number;
  nextImage: string;
  previousImage: string;
  clearCache(): void;
  clearPredictionDetails(): void;
}

export namespace IPredictionInspectorStore {
  export const $: interfaces.ServiceIdentifier<IPredictionInspectorStore> = Symbol('PREDICTION_INSPECTOR_STORE');
}

@injectable()
export class PredictionInspectorStore implements IPredictionInspectorStore {
  jobId = DEFAULT_VALUES.jobId;
  imageId: string | null = DEFAULT_VALUES.imageId;
  images: IModelImageDto[] = DEFAULT_VALUES.images;
  datasetsCache: Map<string, IDatasetDetails> = DEFAULT_VALUES.datasetsCache;
  imageDetailsCache: Map<string, IDatasetPreviewImage> = DEFAULT_VALUES.imageDetailsCache;
  metricsCache: Map<string, IJobImagePredictionDto> = DEFAULT_VALUES.metricsCache;
  annotationsCache: Map<string, IImageAnnotationsResponse | Promise<IImageAnnotationsResponse | ACDLError>> = DEFAULT_VALUES.annotationsCache;
  imagesObjectUrl: Map<string, string> = DEFAULT_VALUES.imagesObjectUrl;
  predictionDetails: IPredictionImage | null = DEFAULT_VALUES.predictionDetails;

  constructor() {
    makeObservable(this, {
      jobId: observable,
      imageId: observable,
      images: observable,
      datasetsCache: observable,
      imageDetailsCache: observable,
      metricsCache: observable,
      annotationsCache: observable,
      imagesObjectUrl: observable,
      predictionDetails: observable,
    });
  }

  clearCache(): void {
    this.datasetsCache = new Map();
    this.imageDetailsCache = new Map();
    this.annotationsCache = new Map();
    this.metricsCache = new Map();
    this.imagesObjectUrl.forEach((fullQuality) => {
      URL.revokeObjectURL(fullQuality);
    });
    this.imagesObjectUrl = new Map();
  }

  clearPredictionDetails(): void {
    this.predictionDetails = DEFAULT_VALUES.predictionDetails;
  }

  get image(): IModelImageDto | null {
    return this.images[this.indexOf] ?? null;
  }

  get indexOf(): number {
    if (this.imageId === null || this.images === null) {
      return -1;
    }

    return this.images.findIndex((image) => image.id === this.imageId);
  }

  get total(): number {
    if (this.imageId === null || this.images === null) {
      return -1;
    }

    return this.images.length;
  }

  get nextImage(): string {
    if (this.imageId === null || this.images === null) {
      return '';
    }

    this.clearPredictionDetails();
    return this.images[this.indexOf + 1].id;
  }

  get previousImage(): string {
    if (this.imageId === null || this.images === null) {
      return '';
    }

    this.clearPredictionDetails();
    return this.images[this.indexOf - 1].id;
  }
}
